import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getDigitalResources,
  createDigitalResource,
  updateDigitalResource,
  deleteDigitalResource,
  getDigitalResourceById,
} from '../services/digitalResourceService';
import Notification from '../components/Common/Notification';

// Create and export the context
export const DigitalResourceContext = createContext();

// Context Provider component
export const DigitalResourceProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [digitalResources, setDigitalResources] = useState([]);
  const [selectedDigitalResource, setSelectedDigitalResource] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  // Function to fetch authentication headers
  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // Function to show notifications
  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  // Function to close notifications
  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  // Function to fetch all digital resources
  const fetchAllDigitalResources = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getDigitalResources(authHeaders);
      setDigitalResources(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching digital resources.';
      showNotification(errorMessage, true);
    }
  };

  // Function to fetch a digital resource by ID
  const fetchDigitalResourceById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getDigitalResourceById(id, authHeaders);
      setSelectedDigitalResource(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching digital resource with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  // Function to add a new digital resource
  const addDigitalResource = async (digitalResourceData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newDigitalResource = await createDigitalResource(digitalResourceData, authHeaders);
      setDigitalResources(prevResources => [...prevResources, newDigitalResource]);
      showNotification('Digital resource added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding digital resource.';
      showNotification(errorMessage, true);
    }
  };

  // Function to edit an existing digital resource
  const editDigitalResource = async (id, digitalResourceData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedDigitalResource = await updateDigitalResource(id, digitalResourceData, authHeaders);
      setDigitalResources(prevResources =>
        prevResources.map(resource => (resource.id === id ? updatedDigitalResource : resource))
      );
      showNotification('Digital resource updated successfully!', false);
      return updatedDigitalResource;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating digital resource.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  // Function to remove a digital resource
  const removeDigitalResource = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteDigitalResource(id, authHeaders);
      setDigitalResources(prevResources => prevResources.filter(resource => resource.id !== id));
      showNotification('Digital resource removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing digital resource.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllDigitalResources();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <DigitalResourceContext.Provider
      value={{
        digitalResources,
        selectedDigitalResource,
        fetchAllDigitalResources,
        fetchDigitalResourceById,
        addDigitalResource,
        editDigitalResource,
        removeDigitalResource,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </DigitalResourceContext.Provider>
  );
};
